import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface TiltShiftParams {
  blur: number;
  inFocusBottom: number;
  inFocusTop: number;
  distortion: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const TiltShift: Filter<TiltShiftParams> = {
  id: "tilt-shift",
  displayName: "Tilt Shift",
  defaultParams: { blur: 15, inFocusBottom: 40, inFocusTop: 60, distortion: 0 },

  apply(input: PixelBuffer, params: TiltShiftParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const blurBottom = params.inFocusBottom / 100;
    const blurTop = params.inFocusTop / 100;
    const maxBlur = Math.round(params.blur);

    for (let y = 0; y < h; y++) {
      const ny = y / h;
      let blurAmount = 0;

      if (ny < blurBottom) {
        blurAmount = maxBlur * (1 - ny / blurBottom);
      } else if (ny > blurTop) {
        blurAmount = maxBlur * ((ny - blurTop) / (1 - blurTop));
      }

      if (blurAmount < 0.5) {
        for (let x = 0; x < w; x++) {
          const o = (y * w + x) * 4;
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
        }
        continue;
      }

      const r = Math.round(blurAmount);
      for (let x = 0; x < w; x++) {
        let sumR = 0, sumG = 0, sumB = 0, sumA = 0, count = 0;

        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            const sx = Math.max(0, Math.min(w - 1, x + dx));
            const sy = Math.max(0, Math.min(h - 1, y + dy));
            const si = (sy * w + sx) * 4;
            sumR += src[si]!; sumG += src[si + 1]!; sumB += src[si + 2]!; sumA += src[si + 3]!;
            count++;
          }
        }

        const o = (y * w + x) * 4;
        if (count > 0) {
          dst[o] = clamp8(Math.round(sumR / count));
          dst[o + 1] = clamp8(Math.round(sumG / count));
          dst[o + 2] = clamp8(Math.round(sumB / count));
          dst[o + 3] = clamp8(Math.round(sumA / count));
        } else {
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
